import React, { useState, useEffect } from 'react';
import { faqData } from '../../data/faqData';
import {
    FaQuestionCircle,
    FaSearch,
    FaStar,
    FaChevronDown,
    FaChevronUp,
    FaArrowRight,
    FaPhone,
    FaEnvelope,
    FaWhatsapp,
    FaHeadset
} from 'react-icons/fa';

const FAQPage = () => {
    const [activeCategory, setActiveCategory] = useState('umum');
    const [searchTerm, setSearchTerm] = useState('');
    const [openQuestions, setOpenQuestions] = useState([]);
    const [showContact, setShowContact] = useState(false);

    const toggleQuestion = (questionId) => {
        setOpenQuestions(prev =>
            prev.includes(questionId)
                ? prev.filter(id => id !== questionId)
                : [...prev, questionId]
        );
    };

    // Buka pertanyaan populer saat pertama load
    useEffect(() => {
        const popularIds = faqData.popularQuestions || [];
        setOpenQuestions(popularIds.slice(0, 3));
    }, []);

    // Filter pertanyaan berdasarkan pencarian
    const getAllQuestions = () => {
        if (!searchTerm) return null;

        const results = [];
        faqData.categories.forEach(cat => {
            cat.questions.forEach(q => {
                if (
                    q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    q.answer.toLowerCase().includes(searchTerm.toLowerCase())
                ) {
                    results.push({ ...q, category: cat.title, categoryId: cat.id });
                }
            });
        });
        return results;
    };

    const searchResults = getAllQuestions();
    const kategoriInfo = faqData.categories.find(cat => cat.id === activeCategory);

    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                    <FaQuestionCircle className="text-5xl text-[#0D9488]" />
                    <h1 className="text-3xl md:text-5xl font-bold text-[#1E3A8A]">
                        FAQ BPKAD Kota Magelang
                    </h1>
                </div>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    Temukan jawaban dari pertanyaan yang sering diajukan seputar layanan BPKAD Kota Magelang
                </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
                <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari pertanyaan..."
                        className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0D9488] focus:border-transparent text-lg"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Search Results */}
            {searchTerm && searchResults ? (
                <div>
                    <h3 className="text-xl font-bold text-[#1E3A8A] mb-6">
                        Hasil Pencarian: "{searchTerm}"
                        <span className="text-gray-500 text-lg font-normal ml-2">
                            ({searchResults.length} ditemukan)
                        </span>
                    </h3>

                    {searchResults.length > 0 ? (
                        <div className="space-y-4">
                            {searchResults.map((item) => (
                                <div key={item.id} className="card-glass">
                                    <button
                                        onClick={() => toggleQuestion(item.id)}
                                        className="w-full text-left flex justify-between items-start p-2"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center mb-2">
                                                <span className="text-xs bg-[#0D9488]/10 text-[#0D9488] px-3 py-1 rounded-full mr-3">
                                                    {item.category}
                                                </span>
                                            </div>
                                            <h4 className="text-lg font-semibold text-[#1E3A8A] pr-8">
                                                {item.question}
                                            </h4>
                                        </div>
                                        {openQuestions.includes(item.id) ? (
                                            <FaChevronUp className="text-[#0D9488] flex-shrink-0 mt-2" />
                                        ) : (
                                            <FaChevronDown className="text-gray-400 flex-shrink-0 mt-2" />
                                        )}
                                    </button>

                                    {openQuestions.includes(item.id) && (
                                        <div className="px-6 pb-6 mt-4 border-t pt-4">
                                            <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                                                {item.answer}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FaSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-500 mb-2">Tidak Ditemukan</h3>
                            <p className="text-gray-400">Tidak ada FAQ yang sesuai dengan pencarian Anda</p>
                            <button
                                onClick={() => setShowContact(true)}
                                className="btn-primary mt-4"
                            >
                                Hubungi Kami
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {/* Category Navigation */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                        {faqData.categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => {
                                    setActiveCategory(cat.id);
                                    // Buka 2 pertanyaan pertama
                                    if (cat.questions.length > 0) {
                                        setOpenQuestions(cat.questions.slice(0, 2).map(q => q.id));
                                    }
                                }}
                                className={`p-6 rounded-xl text-center transition-all duration-300 ${activeCategory === cat.id
                                        ? 'bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] text-white shadow-xl transform scale-105'
                                        : 'bg-white text-gray-600 hover:bg-gray-50 shadow hover:shadow-lg'
                                    }`}
                            >
                                <div className="text-4xl mb-3">{cat.icon}</div>
                                <h3 className="font-bold">{cat.title}</h3>
                                <p className={`text-sm mt-2 ${activeCategory === cat.id ? 'opacity-80' : 'text-gray-400'}`}>
                                    {cat.questions.length} pertanyaan
                                </p>
                            </button>
                        ))}
                    </div>

                    {/* Popular Questions */}
                    <div className="mb-12">
                        <h3 className="text-2xl font-bold text-[#1E3A8A] mb-6 flex items-center">
                            <FaStar className="mr-2 text-yellow-500" />
                            Pertanyaan Populer
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {faqData.popularQuestions.map((qId) => {
                                let question = null;
                                let category = null;

                                for (const cat of faqData.categories) {
                                    const found = cat.questions.find(q => q.id === qId);
                                    if (found) {
                                        question = found;
                                        category = cat;
                                        break;
                                    }
                                }

                                if (!question) return null;

                                return (
                                    <button
                                        key={qId}
                                        onClick={() => {
                                            setActiveCategory(category.id);
                                            setOpenQuestions([qId]);
                                            document.getElementById(`question-${qId}`)?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="card-glass text-left hover:border-[#0D9488] transition-colors p-4"
                                    >
                                        <div className="flex items-start">
                                            <FaQuestionCircle className="text-[#0D9488] mt-1 mr-3 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-semibold text-[#1E3A8A] mb-1 line-clamp-2">
                                                    {question.question}
                                                </p>
                                                <span className="text-xs text-gray-400">{category.title}</span>
                                            </div>
                                            <FaArrowRight className="text-gray-300 ml-auto flex-shrink-0 mt-1" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Questions List */}
                    {kategoriInfo && (
                        <div>
                            <h3 className="text-2xl font-bold text-[#1E3A8A] mb-6">
                                {kategoriInfo.icon} {kategoriInfo.title}
                            </h3>

                            <div className="space-y-4">
                                {kategoriInfo.questions.map((item) => (
                                    <div
                                        key={item.id}
                                        id={`question-${item.id}`}
                                        className="card-glass scroll-mt-24"
                                    >
                                        <button
                                            onClick={() => toggleQuestion(item.id)}
                                            className="w-full text-left flex justify-between items-center p-6"
                                        >
                                            <h4 className="text-lg font-semibold text-[#1E3A8A] pr-8 flex-1">
                                                {item.question}
                                            </h4>
                                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${openQuestions.includes(item.id)
                                                    ? 'bg-[#0D9488] text-white rotate-180'
                                                    : 'bg-gray-100 text-gray-400'
                                                }`}>
                                                <FaChevronDown />
                                            </div>
                                        </button>

                                        {openQuestions.includes(item.id) && (
                                            <div className="px-6 pb-6">
                                                <div className="border-t pt-4"></div>
                                                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                                                    {item.answer}
                                                </div>
                                                <div className="mt-4 flex items-center text-sm text-gray-400">
                                                    <FaQuestionCircle className="mr-2" />
                                                    Apakah jawaban ini membantu?
                                                    <button className="ml-2 text-green-500 hover:text-green-600">👍 Ya</button>
                                                    <button className="ml-2 text-red-500 hover:text-red-600">👎 Tidak</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Contact Section */}
            <div className="mt-16 card-glass bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white">
                <div className="text-center">
                    <FaHeadset className="text-5xl mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-4">Tidak Menemukan Jawaban?</h3>
                    <p className="mb-6 opacity-90">
                        Hubungi kami melalui salah satu kanal di bawah ini
                    </p>
                    <div className="flex flex-col md:flex-row justify-center gap-4">
                        <a
                            href="tel:0293123456"
                            className="bg-white text-[#1E3A8A] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
                        >
                            <FaPhone className="mr-2" />
                            (0293) 123456
                        </a>
                        <a
                            href="https://wa.me/628112600170"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center"
                        >
                            <FaWhatsapp className="mr-2" />
                            Chat WhatsApp
                        </a>
                        <a
                            href="mailto:bpkad@magelangkota.go.id"
                            className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center justify-center border border-white/30"
                        >
                            <FaEnvelope className="mr-2" />
                            Kirim Email
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQPage;